import React, { useState } from "react";
import Table from "../../../core/common/dataTable/index";
import { getEmployeesVerification, getDocument, employeesVerificationUpdate } from "../../../core/data/redux/employeesVerificationSlice";
import { useDispatch, useSelector } from "react-redux";
import { Avatar } from "antd";
import { Link } from "react-router";
import BulkFaceDataRegularizeModal from "./BulkFaceDataRegularizeModal";

import { randomAvatar } from "../../../core/hooks/commonFunctions";
import { useDebounce } from "../../../core/hooks/useDebounce";
import { formatDateTime } from "../../../utils/dateUtils";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AttendanceFilter, { AttendanceFilterValues } from "../../../core/common/AttendanceFilter";
import { formatEmployeeId } from "../../../core/utils/employeeUtils";


interface EmployeesVerificationTableProps {
    userId?: number,
    type: string
    setItem: (item: any) => void

}

const EmployeesVerificationTable = (props: EmployeesVerificationTableProps) => {

    const { type, setItem } = props;
    const dispatch = useDispatch();
    const [query, setQuery] = useState<any>("");
    const employeesVerificationState = useSelector((state: any) => state.employeesVerificationState);
    const [dataList, setDataList] = React.useState<any[]>([]);
    const [originalDataList, setOriginalDataList] = React.useState<any[]>([]); // Store original unfiltered data
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 10
    });
    const [sorter, setSorter] = useState<{ field?: string; order?: string }>({});

    // Bulk selection & modal state
    const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [showBulkModal, setShowBulkModal] = useState<boolean>(false);
    const [modalItems, setModalItems] = useState<any[]>([]);
    const [activeRecordId, setActiveRecordId] = useState<number | undefined>(undefined);
    const tableRef = React.useRef<any>(null);

    // Filter state
    const [filterValues, setFilterValues] = useState<AttendanceFilterValues>({
        wing: "",
        department: "",
        office_location_id: "",
        date_from: "",
        date_to: "",
    });

    const fetchRecords = (pagination: any, query: string, sorter: any) => {
        console.log('sorter', sorter)

        const sortField = (sorter?.field?.length > 1) ? `${sorter?.field?.[1]}` : (sorter?.field?.[0]) ? `${sorter?.field?.[0]}` : undefined;


        dispatch(getEmployeesVerification(
            {
                page: pagination.current,
                limit: pagination.pageSize,
                is_verified: type,
                query: formatEmployeeId(query),
                orderColumn: sortField ? sortField : undefined,
                orderBy: (sorter as any).order
            }
        ));
    }

    const getDocumentUrl = async (record: any) => {
        setItem(record)
        await dispatch(getDocument({ id: record.id }));
    }

    // Frontend filtering function
    const applyFrontendFilters = (data: any[], filters: AttendanceFilterValues) => {
        let filteredData = [...data];

        // Filter by Wing
        if (filters.wing) {
            filteredData = filteredData.filter((item) => {
                const wing = item?.employee?.details?.sap_department_designation_wings?.wing;
                return wing === filters.wing;
            });
        }

        // Filter by Department
        if (filters.department) {
            filteredData = filteredData.filter((item) => {
                const deptId = item?.employee?.details?.sap_department_designation_wings?.department_id ||
                    item?.employee?.details?.department;
                return deptId === filters.department;
            });
        }

        // Filter by Date Range (From Date)
        if (filters.date_from) {
            const fromDate = new Date(filters.date_from);
            fromDate.setHours(0, 0, 0, 0);
            filteredData = filteredData.filter((item) => {
                const createdDate = item?.created_at ? new Date(item.created_at) : null;
                if (!createdDate) return false;
                createdDate.setHours(0, 0, 0, 0);
                return createdDate >= fromDate;
            });
        }

        // Filter by Date Range (To Date)
        if (filters.date_to) {
            const toDate = new Date(filters.date_to);
            toDate.setHours(23, 59, 59, 999);
            filteredData = filteredData.filter((item) => {
                const createdDate = item?.created_at ? new Date(item.created_at) : null;
                if (!createdDate) return false;
                return createdDate <= toDate;
            });
        }

        return filteredData;
    };

    // Handle filter change
    const handleFilterChange = (filters: AttendanceFilterValues) => {
        setFilterValues(filters);
    };

    // Handle search button click - Apply frontend filters
    const handleFilterSearch = () => {
        const filteredData = applyFrontendFilters(originalDataList, filterValues);
        setDataList(filteredData);
        setPagination({
            ...pagination,
            current: 1,
            total: filteredData.length,
        });
    };

    // Handle reset
    const handleFilterReset = () => {
        const resetFilters: AttendanceFilterValues = {
            wing: "",
            department: "",
            office_location_id: "",
            date_from: "",
            date_to: "",
        };
        setFilterValues(resetFilters);
        setDataList(originalDataList);
        setPagination({
            ...pagination,
            current: 1,
            total: originalDataList.length,
        });
    };

    // Download CSV function
    const handleDownloadCSV = () => {
        try {
            const data = dataList;

            if (data.length === 0) {
                toast.warning("No data available to export");
                return;
            }

            const csvHeaders = [
                "Sr. No.",
                "Emp ID",
                "Employee Name",
                "Wing",
                "Department",
                "Designation",
                "Created Date",
                "Is Verified",
            ];

            const csvRows = data.map((item: any, index: number) => {
                const employee = item.employee;
                const details = employee?.details;
                const deptInfo = details?.sap_department_designation_wings;

                return [
                    index + 1,
                    employee?.empId ? parseInt(employee.empId, 10) : "",
                    `${details?.firstname || ""} ${details?.lsastname || ""}`.trim(),
                    deptInfo?.wing_name || "",
                    deptInfo?.department_name || "",
                    deptInfo?.designation_name || "",
                    item.created_at ? formatDateTime(item.created_at) : "",
                    item.is_verified || "",
                ];
            });

            const csvContent = [
                csvHeaders.join(","),
                ...csvRows.map((row: any[]) =>
                    row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
                ),
            ].join("\n");

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `face_data_regularize_${type.toLowerCase()}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success("CSV downloaded successfully");
        } catch (error: any) {
            console.error("Error downloading CSV:", error);
            toast.error("Failed to download CSV");
        }
    };

    React.useEffect(() => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setModalItems([]);
        setActiveRecordId(undefined);
        tableRef.current?.resetSelection([]);
        fetchRecords(pagination, query, sorter);
    }, [type]);



    React.useEffect(() => {
        if (employeesVerificationState.loading === "fulfilled" && employeesVerificationState.action === "update") {
            setSelectedRowKeys([]);
            setSelectedRows([]);
            setModalItems([]);
            setActiveRecordId(undefined);
            tableRef.current?.resetSelection([]);
            fetchRecords(pagination, query, sorter);
        }
        else if (employeesVerificationState.loading === "fulfilled" && employeesVerificationState.action === "list") {
            const result = employeesVerificationState?.data?.data || null;


            if (result.data && result.data.length > 0) {
                const employees = (result?.data || []).map((item: any) => ({
                    ...item,
                    key: item.id,
                }));
                setOriginalDataList(employees); // Store original data
                setDataList(employees);
                setPagination({
                    ...pagination,
                    total: result.total || employees.length,
                    current: result.page || 1,
                    pageSize: result.limit || 10
                });
                // Reset filters when new data is loaded
                setFilterValues({
                    wing: "",
                    department: "",
                    office_location_id: "",
                    date_from: "",
                    date_to: "",
                });
            } else {
                setDataList([]);
                setOriginalDataList([]);
            }
        }
    }, [employeesVerificationState.loading]);

    const columns = [
        {
            title: "Sr. No.",
            dataIndex: "id",
            width: 100,
            render: (text: string, record: any, index: number) => (
                <>
                    <div>
                        {(pagination.current) ? (index + pagination.current * pagination.pageSize - pagination.pageSize + 1) : (index + 1)}
                    </div>
                </>
            ),
            //sorter: (a: any, b: any) => a.id.length - b.id.length,
        },

        {
            title: "EmpId",
            dataIndex: ["employee", "empId"],
            sorter: (a: any, b: any) => a.employee.empId.length - b.employee.empId.length,
            sortDirections: ['ASC', 'DESC', 'ASC'],
            // onHeaderCell: () => ({
            //     'data-field': 'empId', // optional but good if you want a custom hint
            // }),

            render: (employee: any) => `${Number(employee)}` || '',
        },
        {
            title: "Employee Name",
            dataIndex: "employee",
            render: (employee: any, record: any) => {
                const first = employee?.details?.firstname?.trim() || "";
                const last =
                    employee?.details?.lsastname?.trim() || employee?.details?.lastname?.trim() || "";

                if (!first && !last) {
                    return <div style={{ height: "40px" }}></div>;
                }

                const avatarInfo = randomAvatar(first);

                return (
                    <Link
                        to={`/employees/profile/${record.employee_id}`}
                        className="d-flex align-items-center text-decoration-none"
                        style={{ color: "inherit" }}
                    >
                        {employee?.details?.image ? (
                            <Avatar
                                src={`data:image/jpeg;base64,${employee?.details.image}`}
                                className="me-2"
                                style={{
                                    backgroundColor: avatarInfo.bg,
                                    verticalAlign: "middle",
                                    width: "50px",
                                    height: "50px",
                                }}
                            ></Avatar>
                        ) : (
                            <Avatar
                                className="me-2"
                                style={{
                                    backgroundColor: avatarInfo.bg,
                                    verticalAlign: "middle",
                                    width: "50px",
                                    height: "50px",
                                }}
                            >
                                {avatarInfo.initial}
                            </Avatar>
                        )}
                        <div className="text-primary">{`${first} ${last}`.trim()}</div>
                    </Link>
                );
            },
        },





        {
            title: "Wing",
            dataIndex: "employee",
            render: (employee: any) =>
                employee?.details?.sap_department_designation_wings?.wing_name ? `${employee?.details?.sap_department_designation_wings?.wing_name}` : "",
        },
        {
            title: "Department",
            dataIndex: "employee",
            render: (employee: any) =>
                employee?.details?.sap_department_designation_wings?.department_name ? `${employee?.details?.sap_department_designation_wings?.department_name}` : "",
        },
        {
            title: "Designation",
            dataIndex: "employee",
            render: (employee: any) =>
                employee?.details?.sap_department_designation_wings?.designation_name ? `${employee?.details?.sap_department_designation_wings?.designation_name}` : "",
        },
        {
            title: "Created Date",
            dataIndex: "created_at",
            render: (created_at: string) => {
                return created_at ? formatDateTime(created_at) : '';
            },
        },
        {
            title: "Is Verified",
            dataIndex: "is_verified",
            sorter: (a: any, b: any) => a.is_verified.length - b.is_verified.length,
            render: (is_verified: any) => {
                return (
                    <span className={`badge ${(is_verified === 'VERIFIED') ? 'badge-success' : (is_verified === 'REJECTED') ? 'badge-danger' : 'badge-info'} d-inline-flex align-items-center badge-xs`}>
                        {is_verified}
                    </span>
                )
            },
        },
        {
            title: "Actions",
            dataIndex: "actions",
            width: 110,
            render: (text: string, record: any, index: number) => (
                <div className="action-icon d-inline-flex align-items-center gap-1">
                    <button
                        type="button"
                        className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                        onClick={() => handleViewRecord(record)}
                        style={{ fontSize: "12px", padding: "4px 10px", borderRadius: "5px" }}
                    >
                        <i className="ti ti-eye" /> View
                    </button>
                    <Link
                        to={'#'}
                        title="View other photos"
                        data-bs-toggle="modal"
                        data-inert={true}
                        data-bs-target="#other-doc"
                        onClick={() => getDocumentUrl(record)}
                        style={{ color: "#6b7280", padding: "4px" }}
                    >
                        <i className="ti ti-files fs-16" />
                    </Link>
                </div>
            ),
        }
    ];

    // const handlePagination = (pagination: any, filter: any, sorter: any) => {

    //     //const sortField = sorter.field === "employee.empId" ? "empId" : sorter.field;



    //     setPagination({
    //         current: pagination.page,
    //         pageSize: pagination.pageSize,
    //         total: pagination.total || 10,

    //     });
    //     fetchRecords(pagination, query, sorter);
    // };


    const handlePagination = (newPagination: any) => {
        setPagination((prev) => {
            const isPageSizeChanged = prev.pageSize !== newPagination.pageSize;
            const updatedPagination = {
                current: isPageSizeChanged ? 1 : newPagination.current || 1,
                pageSize: newPagination.pageSize,
                total: newPagination.total || prev.total,
            };
            fetchRecords(updatedPagination, query, sorter);
            return updatedPagination;
        });
    };


    const debouncedFetchRecords = useDebounce(
        (pagination: any, query: string, sorter: any) => {
            fetchRecords(pagination, query, sorter);
        },
        500
    );

    const handleViewRecord = (record: any) => {
        const recId = record.id ?? record.key;
        // Instead of only 1 record, load all pending records from table so all are selected in modal
        const allItems = dataList && dataList.length > 0 ? dataList : [record];
        setModalItems(allItems);
        setActiveRecordId(recId);
        setShowBulkModal(true);
    };

    const handleOpenBulkModal = () => {
        // If user explicitly checked rows in table, use them; otherwise default to all records
        const itemsToUse = selectedRows.length > 0 ? selectedRows : (dataList && dataList.length > 0 ? dataList : []);
        if (itemsToUse.length === 0) {
            toast.warning("No records available to regularize.");
            return;
        }
        setModalItems(itemsToUse);
        setActiveRecordId(itemsToUse[0]?.id ?? itemsToUse[0]?.key);
        setShowBulkModal(true);
    };

    const handleSearch = (query: string) => {
        setQuery(query);
        const searchPagination = { ...pagination, current: 1 };
        setPagination(searchPagination);
        debouncedFetchRecords(searchPagination, query, sorter);
    };
    return (
        <div>
            <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                    <div className="d-flex align-items-center gap-3">
                        <h4 className="mb-4 mt-3">{type} Face Data Regularize</h4>
                        {type === "PENDING" && (
                            <button
                                type="button"
                                className="btn btn-sm mb-4 mt-3 d-flex align-items-center gap-2"
                                disabled={dataList.length === 0}
                                onClick={handleOpenBulkModal}
                                style={{
                                    borderRadius: "6px",
                                    fontWeight: 500,
                                    backgroundColor: "#C0282C",
                                    borderColor: "#C0282C",
                                    color: "#fff",
                                    cursor: dataList.length > 0 ? "pointer" : "not-allowed",
                                    transition: "all 0.2s ease",
                                    padding: "6px 14px",
                                }}
                            >
                                <i className="ti ti-scan fs-16" />
                                Bulk Regularize {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : `All (${dataList.length})`}
                            </button>
                        )}
                    </div>
                </div>

                {/* Bulk Selection Banner */}
                {type === "PENDING" && selectedRowKeys.length > 0 && (
                    <div
                        className="d-flex align-items-center justify-content-between mx-3 mt-3 px-3 py-2"
                        style={{
                            backgroundColor: "#FEF2F2",
                            border: "1px solid #FECACA",
                            borderRadius: "8px",
                        }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="ti ti-scan" style={{ color: "#C0282C", fontSize: "18px" }} />
                            <span style={{ fontSize: "13px", color: "#991B1B", fontWeight: 500 }}>
                                <strong>{selectedRowKeys.length}</strong> face verification record
                                {selectedRowKeys.length > 1 ? "s" : ""} selected for bulk regularization
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-sm d-flex align-items-center gap-1"
                                onClick={handleOpenBulkModal}
                                style={{
                                    backgroundColor: "#C0282C",
                                    borderColor: "#C0282C",
                                    color: "#fff",
                                    fontWeight: 500,
                                    fontSize: "12px",
                                    padding: "4px 12px",
                                    borderRadius: "6px",
                                }}
                            >
                                <i className="ti ti-check" />
                                Regularize Selected ({selectedRowKeys.length})
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                style={{ fontSize: "12px", padding: "4px 10px", borderRadius: "6px" }}
                                onClick={() => {
                                    setSelectedRowKeys([]);
                                    setSelectedRows([]);
                                    setModalItems([]);
                                    setActiveRecordId(undefined);
                                    tableRef.current?.resetSelection([]);
                                }}
                            >
                                Clear Selection
                            </button>
                        </div>
                    </div>
                )}

                {/* Filter Section */}
                <div className="px-3 pt-3">
                    <AttendanceFilter
                        filterValues={filterValues}
                        onFilterChange={handleFilterChange}
                        onSearch={handleFilterSearch}
                        onReset={handleFilterReset}
                        onDownloadCSV={handleDownloadCSV}
                        showDownload={true}
                        attendanceData={originalDataList}
                        showLocation={false}
                    />
                </div>
                <div className="card-body p-0 py-3">
                    <Table
                        ref={tableRef}
                        columns={columns}
                        dataSource={dataList}
                        Selection={type === "PENDING"}
                        onRowSelection={(keys: any[]) => {
                            setSelectedRowKeys(keys);
                            const selected = dataList.filter((item: any) =>
                                keys.includes(item.id ?? item.key)
                            );
                            setSelectedRows(selected);
                        }}
                        getCheckboxProps={(record: any) => ({
                            disabled: record.is_verified !== "PENDING",
                        })}
                        paginationConfig={pagination}
                        handlePagination={handlePagination}
                        loading={employeesVerificationState.loading === "pending"}

                    >
                        <label>
                            {" "}
                            <input
                                type="search"
                                className="form-control form-control-sm"
                                placeholder="Search by Emp ID"
                                aria-controls="DataTables_Table_0"
                                value={query}
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{ width: 200, float: 'right' }}
                            />
                        </label>
                    </Table>
                </div>
            </div>
            <div className="row align-items-center">
                <div className="col-md-12">
                    <div className="datatable-paginate mt-4" />
                </div>
            </div>

            <BulkFaceDataRegularizeModal
                open={showBulkModal}
                items={modalItems.length > 0 ? modalItems : selectedRows}
                initialActiveId={activeRecordId}
                onClose={() => {
                    setShowBulkModal(false);
                    setModalItems([]);
                    setActiveRecordId(undefined);
                }}
                onSuccess={() => {
                    setSelectedRowKeys([]);
                    setSelectedRows([]);
                    setModalItems([]);
                    setActiveRecordId(undefined);
                    tableRef.current?.resetSelection([]);
                    fetchRecords(pagination, query, sorter);
                }}
            />
        </div>
    );
}

export default EmployeesVerificationTable; 